import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../api";
import { ArrowLeft } from "lucide-react";

const ROOT_DOMAIN = process.env.REACT_APP_PLATFORM_ROOT_DOMAIN || "filagestao.com.br";

const SEGMENTS = [
  "Salão de beleza", "Esmalteria", "Barbearia", "Clínica",
  "Estética", "Studio", "Outros",
];

async function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function SuperTenantEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/super/tenants/${id}`);
      setT(data);
      setForm({
        name: data.name || "",
        display_name: data.display_name || "",
        tv_display_name: data.tv_display_name || "",
        segment: data.segment || "",
        email: data.email || "",
        whatsapp: data.whatsapp || "",
        city: data.city || "",
        state: data.state || "",
        logo_url: data.logo_url || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) return toast.error("PNG/JPG/WEBP");
    if (f.size > 600 * 1024) return toast.error("Logo > 600KB");
    set("logo_url", await readFileAsDataURL(f));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/super/tenants/${id}`, form);
      toast.success("Alterações salvas");
      nav("/super/clientes");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!t) return <div className="text-sm text-zinc-500">Carregando…</div>;

  const inputCls = "w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all";

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="super-tenant-edit">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-brand-600">
        <ArrowLeft size={14} /> Voltar
      </button>
      <div>
        <h1 className="text-2xl font-semibold">Editar Cliente</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Slug: <code className="rounded bg-brand-50 border border-brand-100 px-2 py-0.5 text-brand-700">{t.slug}</code>
          {t.client_code && (
            <> · Código: <code data-testid="edit-client-code" className="rounded bg-brand-50 border border-brand-100 px-2 py-0.5 text-brand-700">Cliente #{t.client_code}</code></>
          )}
          <a href={`https://${ROOT_DOMAIN}/${t.slug}`} target="_blank" rel="noreferrer" className="ml-2 text-brand-600 hover:underline">
            https://{ROOT_DOMAIN}/{t.slug}
          </a>
        </p>
        {t.stats && (
          <div className="mt-2 flex gap-4 text-xs text-zinc-500">
            <span>Admins: {t.stats.admins}</span>
            <span>Profissionais: {t.stats.professionals}</span>
            <span>Categorias: {t.stats.categories}</span>
          </div>
        )}
      </div>

      <form onSubmit={save} className="space-y-6">
        <section className="rounded-2xl border border-zinc-200 bg-white p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-500">Empresa</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome"><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="edit-name" /></Field>
            <Field label="Nome de exibição"><input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} className={inputCls} data-testid="edit-display" /></Field>
            <Field label="Nome no Painel TV"><input value={form.tv_display_name} onChange={(e) => set("tv_display_name", e.target.value)} className={inputCls} data-testid="edit-tv" /></Field>
            <Field label="Segmento">
              <select value={form.segment} onChange={(e) => set("segment", e.target.value)} className={inputCls} data-testid="edit-segment">
                <option value="">— não definido —</option>
                {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="E-mail"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
            <Field label="WhatsApp"><input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></Field>
            <Field label="Cidade"><input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Field>
            <Field label="Estado"><input maxLength={2} value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} /></Field>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">Logo</label>
            <div className="flex items-center gap-4">
              {form.logo_url && <img src={form.logo_url} alt="preview" className="h-14 w-14 rounded-full object-cover border border-zinc-200" />}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} data-testid="edit-logo"
                className="text-sm text-zinc-600 file:mr-3 file:rounded-full file:border-0 file:bg-brand-500 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-brand-600" />
              {form.logo_url && (
                <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-red-500 hover:underline">Remover logo</button>
              )}
            </div>
          </div>
        </section>
        <button type="submit" disabled={busy} data-testid="edit-submit" className="rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 disabled:opacity-60 transition-all">
          {busy ? "Salvando…" : "Salvar alterações"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}
